import React, { createRef } from "react";
import PropTypes from "prop-types";
import { inject, observer } from "mobx-react";
/** UI Component library */
import Scrollbar from "react-scrollbars-custom";
import classnames from "classnames";
import { TabFooter } from "component/atoms/tabComponents";
import HotkeyField from "component/atoms/HotkeyField";
import Helper from "helper";
import { MODIFIER_KEYS } from "hotkeysConfig";
import SVGIcon from "assets/icon/";
import "./HotkeysPreferences.styl";

function hasClass(ele, cls) {
  return !!ele.className.match(new RegExp("(\\s|^)" + cls + "(\\s|$)"));
}

function addClass(ele, cls) {
  if (!hasClass(ele, cls)) ele.className += " " + cls;
}

function removeClass(ele, cls) {
  if (hasClass(ele, cls)) {
    var reg = new RegExp("(\\s|^)" + cls + "(\\s|$)");
    ele.className = ele.className.replace(reg, " ");
  }
}

/**
 * 한개의 단축키 목록을 두 개의 배열로 분할
 *
 * @param {array} hotkeys    단축키 목록
 * @returns {array} array    두 개로 분할된 단축키 배열
 */
const splitHotkeys = (hotkeys) => {
  const splitedHotkeys = [];
  const arrayHotkeys = Object.entries(hotkeys);

  if (arrayHotkeys.length) {
    const halfwayThrough = Math.ceil(arrayHotkeys.length / 2);
    splitedHotkeys.push(arrayHotkeys.slice(0, halfwayThrough));
    splitedHotkeys.push(
      arrayHotkeys.slice(halfwayThrough, arrayHotkeys.length)
    );
  }

  return splitedHotkeys;
};

const propTypes = {
  //keyMap: PropTypes.array,
  keyMap: PropTypes.object,
  onClose: PropTypes.func,
};

const defaultProps = {
  //keyMap: [],
  keyMap: {},
  onClose: () => {},
};

/**
 * HotkeysPreferences tab
 * It renders all hotkeys displayed into columns/rows
 *
 * It stores current state and whenever it changes, component messages parent of new value (through function callback)
 * @param {object} props component props
 * @param {string} props.onClose
 */
@inject("hotkeyStore")
@observer
class HotkeysPreferences extends React.Component {
  element = createRef();

  constructor(props) {
    super(props);
    this.state = {
      keyMap: props.keyMap || [],
      defaultKeyMap: props.defaultKeyMap || [],
      errors: {},
    };
  }

  componentDidMount() {
    this.element.focus();
    this.element.addEventListener("keydown", this.onKeydownHanlder);
  }

  componentWillUnmount() {
    this.element.removeEventListener("keydown", this.onKeydownHanlder);
  }

  onKeydownHanlder = (event) => {
    // if (event.keyCode === 27) {
    //   this.onClose();
    // }
  };

  onFocus = (event) => {
    const parentElement = event.target.parentNode;
    addClass(parentElement, "hotkey-focus");
  };
  onBlur = (event) => {
    const parentElement = event.target.parentNode;
    removeClass(parentElement, "hotkey-focus");
  };

  onResetPreferences = () => {
    this.resetAsDefaultKeyMap();
  };

  onSave = () => {
    this.updateKeyMapToStore();
    this.saveKeyMapToLocalStorage();
    this.onClose();
    // snackbar.show({
    //   message: t("SaveMessage"),
    //   type: "success",
    // });
  };

  onClose = () => {
    const { onClose } = this.props;
    onClose();
  };

  onHotkeyChanged = (name, hotkeyDefinition, keys) => {
    const {
      errorMessage,
      hasError,
      type,
    } = Helper.Validator.validateCommandKey({
      commandName: name,
      pressedKeys: keys,
      hotkeys: this.state.keyMap,
    });

    let keyString = "";
    if (Helper.Validator.isArray(keys) === true) {
      keyString = keys.join("+");
    } else {
      keyString = keys;
    }

    this.setState((prevState) => ({
      keyMap: {
        ...prevState.keyMap,
        [name]: {
          ...prevState.keyMap[name],
          sequence: keyString,
          //sequence: keys[0],
        },
      },
      errors: {
        ...prevState.errors,
        [name]: {
          errorMessage: errorMessage,
          hasError: hasError,
          type: type,
        },
      },
    }));
  };

  resetAsDefaultKeyMap = () => {
    const { defaultKeyMap } = this.props;
    this.setState({
      keyMap: defaultKeyMap,
    });
  };

  updateKeyMapToStore = () => {
    const { hotkeyStore } = this.props;
    const { keyMap } = this.state;
    hotkeyStore.setHotkeys(keyMap);
  };

  saveKeyMapToLocalStorage = () => {
    const { keyMap } = this.state;
    localStorage.setItem("hotkey-definitions", JSON.stringify(keyMap));
  };

  getHotKeyArray(keyMap) {
    return splitHotkeys(keyMap);
  }

  render() {
    const { keyMap, errors } = this.state;

    //const hasErrors = Object.keys(errors).some((key) => !!errors[key].hasError);
    const hasErrors = Object.keys(errors).some((key) => errors[key].hasError);
    const hasHotkeys = Object.keys(keyMap).length;
    const splitedHotkeys = this.getHotKeyArray(keyMap);

    return (
      <React.Fragment>
        <div
          ref={(input) => {
            this.element = input;
          }}
          className="HotkeysPreferences"
        >
          {hasHotkeys ? (
            <Scrollbar
              noScrollX={true}
              noDefaultStyles
              permanentTrackY={false}
              onContextMenu={(event) => event.preventDefault()}
            >
              <div className="hotkeyTable">
                {splitedHotkeys.map((hotkeys, index) => {
                  return (
                    <div
                      className={classnames(
                        "hotkeyColumn",
                        index === 0 ? "first" : "second"
                      )}
                      key={index}
                    >
                      <div className="hotkeyHeader">
                        {/* <div className="headerItemText text-right"> */}
                        <div className="headerItemText text-center">
                          Function
                        </div>
                        <div className="headerItemText text-center">
                          Shortcut
                        </div>
                      </div>
                      {hotkeys.map((hotkeyItem, index) => {
                        const isEven = index % 2 === 0;
                        const name = hotkeyItem[0];
                        const description = hotkeyItem[1].description;
                        // FIXME
                        //const keySequences = hotkeyItem[1].sequences[0].sequence;
                        const keySequences = hotkeyItem[1].sequence;
                        let keys = [keySequences];
                        if (Helper.Validator.isArray(keySequences) === true) {
                          keys = [keySequences[0]];
                        }
                        //const keys = keyMap[name].sequences.join(" ");
                        // const { keys, label } = hotkeyDefinition;
                        //const { errorMessage, type } = errors[name];
                        const errorDetail = errors[name];
                        let isEmptyError = false;
                        if (
                          Helper.Validator.isDefined(errorDetail) === true &&
                          errorDetail.hasOwnProperty("type") === true
                        ) {
                          isEmptyError =
                            errorDetail.type === "EMPTY-ERROR" ? true : false;
                        }

                        const handleChange = (keys) => {
                          this.onHotkeyChanged(name, description, keys);
                        };

                        return (
                          <div
                            key={name}
                            className={classnames(
                              "hotkeyRow",
                              isEven ? "even-row" : "odd-row"
                            )}
                          >
                            <div
                              className={classnames(
                                "hotkeyLabel",
                                "text-center"
                              )}
                            >
                              {description}
                            </div>
                            <div
                              data-key="defaultTool"
                              className={classnames(
                                "wrapperHotkeyInput",
                                errorDetail && errorDetail.errorMessage
                                  ? "stateError"
                                  : ""
                              )}
                            >
                              <HotkeyField
                                keys={keys}
                                modifier_keys={MODIFIER_KEYS}
                                handleChange={handleChange}
                                onFocus={this.onFocus}
                                onBlur={this.onBlur}
                                classNames={classnames(
                                  "hotkeyInput",
                                  !(errorDetail && errorDetail.errorMessage)
                                    ? "text-center"
                                    : isEmptyError === true
                                    ? "text-center"
                                    : "text-left"
                                )}
                              />
                              <span
                                className={classnames(
                                  "errorMessage",
                                  isEmptyError === true
                                    ? "text-center"
                                    : "text-right"
                                )}
                              >
                                {errorDetail && errorDetail.errorMessage && (
                                  <SVGIcon.SVGSmallWarningIcon />
                                )}
                                {errorDetail && errorDetail.errorMessage}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </Scrollbar>
          ) : (
            "Hotkeys definitions is empty"
          )}{" "}
        </div>
        <TabFooter>
          <button
            className="btn btn-danger pull-left"
            data-cy="reset-default-btn"
            onClick={this.onResetPreferences}
          >
            {"Reset to Defaults"}
          </button>
          <div>
            <button
              className="btn btn-primary"
              data-cy="save-btn"
              disabled={hasErrors}
              onClick={this.onSave}
            >
              {"Save"}
            </button>
            <button
              onClick={this.onClose}
              className="btn btn-cancel"
              data-cy="cancel-btn"
            >
              {"Cancel"}
            </button>
          </div>
        </TabFooter>
      </React.Fragment>
    );
  }
}

HotkeysPreferences.propTypes = propTypes;
HotkeysPreferences.defaultProps = defaultProps;

export { HotkeysPreferences };
